
import '../styles/Watermark.css';
import {useEffect, useRef, useCallback, forwardRef, useImperativeHandle} from "react";
import type CreativeEngine from "@cesdk/engine";

import watermarkSvg from "../assets/watermark.svg";

// Page height (px) at which the watermark is shown at full (natural) size; scales down below this
const WM_FULL_SIZE_PAGE_HEIGHT = 900;

const WM_BASE_WIDTH = 490;
const WM_ASPECT_RATIO = 303 / 99.5;
const WM_MAX_PAGE_WIDTH_RATIO = 0.52;
const WM_LEFT_OVERHANG_RATIO = 0.061;
const WM_BOTTOM_RATIO = 0.025;

interface WatermarkProps {
    senderName?: string;
    senderEmail?: string;
    engine?: CreativeEngine | null;
    activePage?: number | null;
}

export interface WatermarkHandle {
    positionWatermark: () => void;
}

const Watermark = forwardRef<WatermarkHandle, WatermarkProps>(({senderName, senderEmail, engine, activePage}: WatermarkProps, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const markRef = useRef<HTMLDivElement>(null);
    const displaySender = senderName?.trim() || senderEmail?.trim();

    const positionWatermark = useCallback((): void => {
        const container = containerRef.current;
        const mark = markRef.current;
        if (!container || !mark || !engine || !activePage) return;

        const canvas = engine.element as HTMLCanvasElement | undefined;
        if (!canvas) { container.style.display = 'none'; return; }

        const raw = engine.block.getScreenSpaceBoundingBoxXYWH([activePage]);
        if (!raw) { container.style.display = 'none'; return; }

        const pageX = raw[0];
        const pageY = raw[1];
        const pageWidth = raw[2];
        const pageHeight = raw[3];

        const canvasRect = canvas.getBoundingClientRect();

        // Position container to cover the page area
        const pageLeft = canvasRect.left + pageX;
        const pageTop = canvasRect.top + pageY;

        container.style.display = 'block';
        container.style.left = `${pageLeft}px`;
        container.style.top = `${pageTop}px`;
        container.style.width = `${pageWidth}px`;
        container.style.height = `${pageHeight}px`;

        // Scale the mark at the same rate as the rendered page, with a cap for narrow layouts.
        const markScale = Math.min(1, pageHeight / WM_FULL_SIZE_PAGE_HEIGHT) * 1.2;
        const markWidth = Math.min(WM_BASE_WIDTH * markScale, pageWidth * WM_MAX_PAGE_WIDTH_RATIO);
        const markHeight = markWidth / WM_ASPECT_RATIO;
        const markLeft = -(markWidth * WM_LEFT_OVERHANG_RATIO);
        const markBottom = Math.max(8, pageHeight * WM_BOTTOM_RATIO);

        mark.style.width = `${markWidth}px`;
        mark.style.height = `${markHeight}px`;
        mark.style.left = `${markLeft}px`;
        mark.style.bottom = `${markBottom}px`;
        mark.style.top = 'auto';

        container.style.setProperty('--wm-text-font-size', `${Math.max(10, Math.min(32, pageWidth * 0.012))}px`);
    }, [engine, activePage]);

    useImperativeHandle(ref, () => ({ positionWatermark }), [positionWatermark]);

    useEffect(() => {
        if (!engine) return;

        const handleResize = () => positionWatermark();
        const handleScroll = () => positionWatermark();

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        const unsubscribeZoom = engine.scene.onZoomLevelChanged(handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
            if (unsubscribeZoom) {
                try { unsubscribeZoom(); } catch {}
            }
        };
    }, [engine, positionWatermark]);

    return (
        <div id="wm-container" ref={containerRef} aria-hidden="true">
            <div id="wm-mark" ref={markRef}>
                <img src={watermarkSvg} alt="" draggable={false}/>
                {displaySender && (
                    <div className="watermarkText">
                        <span>Sent by</span>
                        <span>{displaySender}</span>
                    </div>
                )}
            </div>
        </div>
    );
});

Watermark.displayName = 'Watermark';

export default Watermark;
